import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";

/**
 * Responsive activity trend chart, reusing whatever monthly series the
 * active role's dashboard endpoint already returns (see roleConfig.js).
 *
 * BUG FIX HISTORY (why this is a fixed height, not a dynamic one that
 * matches the sibling Recent Activity panel's height pixel-for-pixel):
 * 1. `chart.height: "100%"` is unreliable when the parent's height comes
 *    from a CSS flex-stretch rather than an explicit height - ApexCharts
 *    reads the parent's height at chart-creation time, which can be
 *    captured before the flex layout has resolved, producing a near-zero
 *    chart.
 * 2. A one-time `getBoundingClientRect()` measurement is ALSO unreliable
 *    here specifically because the sibling Recent Activity panel's real
 *    height depends on its own separate, slower network fetch, so the
 *    measurement taken when this component's effect runs is often stale.
 * 3. Watching the chart's own container (or even the outer panel) with a
 *    ResizeObserver and calling `chart.updateOptions({ height })` created an
 *    UNBOUNDED FEEDBACK LOOP: this row uses `align-items: stretch`, which
 *    sets both panels' heights to the tallest child's natural CONTENT
 *    height. Growing the chart's height grows this panel's natural content
 *    height, which grows the row's stretch height (since it's now the
 *    tallest), which the observer sees as a resize and grows the chart
 *    again - climbing without bound (confirmed via debug logging on two
 *    different elements - height climbed indefinitely, e.g. 280 -> 295 ->
 *    ... -> 5670+). This is inherent to `align-items: stretch` and isn't
 *    fixable by choosing a different element to observe.
 *
 * Fixed by giving the chart a solid, generous FIXED height instead - no
 * runtime measurement or observation at all. It won't pixel-match the
 * Recent Activity panel's height exactly, but it's robust, predictable, and
 * looks proportional without any risk of layout loops.
 *
 * Visual style: soft gradient area fill, smoothed curve, subtle point
 * markers, and a light dashed grid - a bit more polished than a flat single-
 * colour area chart, while staying restrained (no heavy shadows/borders).
 */
export default function PrimaryInsight({ title, subtitle, categories, series, colors }) {
  const containerRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 576 : false
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 576);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const hasData = Array.isArray(categories) && categories.length > 0;
  const palette = colors && colors.length ? colors : ["#17c191", "#5aa7f5"];
  const height = isNarrow ? 300 : 400;

  useEffect(() => {
    if (!hasData || !containerRef.current) return undefined;

    const chart = new ApexCharts(containerRef.current, {
      chart: {
        type: "area",
        height,
        width: "100%",
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "inherit",
        animations: { easing: "easeinout", speed: 500 }
      },
      series,
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "11px", colors: "#a1a5b7" } }
      },
      yaxis: {
        min: 0,
        labels: { style: { fontSize: "11px", colors: "#a1a5b7" } }
      },
      grid: {
        borderColor: "#f1f2f6",
        strokeDashArray: 4,
        padding: { left: 8, right: 8 },
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } }
      },
      colors: palette,
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.02,
          stops: [0, 90, 100]
        }
      },
      stroke: { curve: "smooth", width: 2.5 },
      markers: {
        size: 0,
        hover: { size: 5 },
        strokeWidth: 2,
        strokeColors: "#fff"
      },
      legend: {
        position: "bottom",
        fontSize: "12px",
        markers: { radius: 999 },
        itemMargin: { horizontal: 10 }
      },
      dataLabels: { enabled: false },
      tooltip: { shared: true, y: { formatter: v => `${v}` } }
    });

    chart.render();

    return () => {
      chart.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, hasData, JSON.stringify(categories), JSON.stringify(series), JSON.stringify(palette)]);

  return (
    <div className="dashboard-panel">
      <div className="dashboard-panel__header">
        <h2 className="dashboard-panel__title">{title}</h2>
        {subtitle && <p className="dashboard-panel__subtitle">{subtitle}</p>}
      </div>

      {!hasData ? (
        <div className="dashboard-empty">No activity recorded yet.</div>
      ) : (
        <>
          <div className="dashboard-chart" ref={containerRef} />
          {/* Accessible textual summary - the chart's data is not conveyed by hover/tooltip alone. */}
          <p className="sr-only">
            {series
              .map(s => `${s.name}: ${s.data[s.data.length - 1]} in the most recent month (${categories[categories.length - 1]})`)
              .join(". ")}
          </p>
        </>
      )}
    </div>
  );
}
